import { createColDefs } from '@fortem-monorepo/react-ag'
import { valueFormatterFromMap } from '@fortem-monorepo/react-ag'
import { useUserContext } from '@fortem-monorepo/react-spa'

import { audit } from '@/ag-grid/columns/audit/col-def'
import { counterparty_id } from '@/ag-grid/columns/counterparty-id/col-def'
import { currency_id } from '@/ag-grid/columns/currency-id/col-def'
import { instruction_date } from '@/ag-grid/columns/instruction-date/col-def'
import { investment_bucket_id } from '@/ag-grid/columns/investment-bucket-id/col-def'
import { strategy_id } from '@/ag-grid/columns/strategy-id/col-def'
import { tags } from '@/ag-grid/columns/tags/col-def'
import { top_level_bucket_id } from '@/ag-grid/columns/top-level-bucket-id/col-def'
import { trade_type } from '@/ag-grid/columns/trade-type/coldef'
import { wrapper_bucket_id } from '@/ag-grid/columns/wrapper-bucket-id/col-def'
import {
  active,
  accrued_interests,
  comment,
  instrument_type,
  notional,
  price,
  quantity,
  settlement_date,
  trade_date,
  trade_datetime_utc,
  unique_identifier,
} from '@/pages/trading/booked-trades/grids/shared-coldefs'
import { useTradingData } from '@/react-query/queries/static'

export const createNewDBColDefs = ({
  ctx,
  tradeData,
}: {
  ctx: ReturnType<typeof useUserContext>
  tradeData: ReturnType<typeof useTradingData>
}) => {
  const {
    currencies,
    counterparties,
    strategies,
    topLevelBuckets,
    investmentBuckets,
    wrapperBuckets,
  } = tradeData.maps

  const canEdit = ctx.hasPermissions((permissions) => ({ permissions: [permissions.WRITE_TRADES] }))

  return createColDefs([
    audit,
    {
      field: 'trade_id',
      hide: true,
    },
    trade_date,
    {
      ...strategy_id,
      filterParams: {
        valueFormatter: valueFormatterFromMap(strategies),
      },
    },
    {
      field: 'strategy_name',
      headerName: 'Strategy Name',
      hide: true,
    },
    {
      ...active,
    },
    unique_identifier,
    instrument_type,
    {
      ...price,
      editable: canEdit,
    },
    {
      ...quantity,
      editable: canEdit,
    },
    {
      ...accrued_interests,
      editable: ({ data }) => canEdit && data?.instrument_type_id === 2,
    },
    {
      field: 'trade_side',
      headerName: 'Trade Side',
    },
    {
      ...notional,
      editable: canEdit,
    },
    {
      ...currency_id,
      editable: canEdit,
      filterParams: {
        valueFormatter: valueFormatterFromMap(currencies),
      },
    },
    {
      field: 'trade_currency',
      headerName: 'Trade Ccy',
      hide: true,
    },
    {
      ...instruction_date,
      editable: canEdit,
    },
    {
      ...trade_datetime_utc,
      editable: canEdit,
    },
    {
      ...settlement_date,
      editable: canEdit,
    },
    {
      ...counterparty_id,
      editable: canEdit,
      filterParams: {
        valueFormatter: valueFormatterFromMap(counterparties),
      },
    },
    {
      field: 'counterparty_name',
      headerName: 'Counterparty Name',
      hide: true,
    },
    {
      ...top_level_bucket_id,
      width: 150,
      editable: canEdit,
      filterParams: {
        valueFormatter: valueFormatterFromMap(topLevelBuckets),
      },
    },
    {
      field: 'top_level_bucket',
      headerName: 'Top Level Bucket Name',
      hide: true,
    },
    {
      ...investment_bucket_id,
      width: 150,
      editable: canEdit,
      filterParams: {
        valueFormatter: valueFormatterFromMap(investmentBuckets),
      },
    },
    {
      field: 'investment_bucket',
      headerName: 'Investment Bucket Name',
      hide: true,
    },
    {
      ...wrapper_bucket_id,
      width: 150,
      editable: canEdit,
      filterParams: {
        valueFormatter: valueFormatterFromMap(wrapperBuckets),
      },
    },
    {
      field: 'wrapper_bucket',
      headerName: 'Wrapper Bucket Name',
      hide: true,
    },
    {
      field: 'locked_ytm',
      headerName: 'Yield Lock',
      cellClass: 'text-center',
      width: 125,
      editable: canEdit,
    },
    {
      ...trade_type,
      editable: canEdit,
    },
    {
      field: 'reference_instrument_unique_identifier',
      headerName: 'Ref Instrument',
      cellClass: 'text-center',
      width: 150,
    },
    {
      ...comment,
      editable: canEdit,
    },
    {
      ...tags,
      editable: canEdit,
    },
    {
      field: 'trader_name',
      headerName: 'Trader Name',
      hide: true,
    },
  ])
}
